import React from 'react';
import { motion } from 'framer-motion';
import { Link as ScrollLink } from 'react-scroll';

const Badge = () => (
  <motion.div
    initial={{ opacity: 0, y: -8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.15 }}
    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-900/30 mb-6"
  >
    <span className="h-2 w-2 rounded-full bg-blue-600" />
    <span className="text-blue-700 dark:text-blue-400 text-sm font-medium">Professional Training & Consultancy</span>
  </motion.div>
);

const Headline = () => (
  <motion.h1
    initial={{ opacity: 0, y: -8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.25 }}
    className="text-4xl sm:text-5xl lg:text-6xl text-blue-900 dark:text-white font-semibold tracking-tight"
  >
    Build high‑performing teams with <span className="text-[#1A759E] dark:text-blue-400">Viso</span> Analytics
  </motion.h1>
);

const Subcopy = () => (
  <motion.p
    initial={{ opacity: 0, y: -6 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.35 }}
    className="mt-6 text-lg sm:text-xl text-gray-600 dark:text-gray-300 leading-relaxed"
  >
    Enterprise-grade training and consulting across Microsoft 365, AI/ML, and analytics — tailored to your goals and delivered with measurable outcomes.
  </motion.p>
);

const CTAs = () => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.45 }}
    className={`mt-10 flex flex-wrap items-center justify-center gap-3 sm:gap-4`}
  >
    <ScrollLink
      to="contact"
      smooth
      duration={600}
      offset={-80}
      className="inline-flex items-center rounded-xl bg-blue-600 px-6 py-3 text-white text-sm sm:text-base font-medium shadow-lg shadow-blue-500/30 hover:bg-blue-700 hover:shadow-blue-500/40 transition-all cursor-pointer"
    >
      Get in touch
    </ScrollLink>
    <ScrollLink
      to="services"
      smooth
      duration={600}
      offset={-80}
      className="inline-flex items-center rounded-xl border-2 border-blue-100 px-6 py-3 text-blue-700 text-sm sm:text-base font-medium hover:bg-blue-50 hover:border-blue-200 transition-colors cursor-pointer"
    >
      Explore services
    </ScrollLink>
  </motion.div>
);

const Hero = () => (
  <section name="home" className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden px-4 sm:px-6 text-center">
    <div className="absolute inset-0 -z-10">
      <div className="absolute inset-0 bg-gradient-to-b from-white via-blue-50/50 to-white dark:from-gray-900 dark:via-blue-900/10 dark:to-gray-900" />
      <div className="pointer-events-none absolute top-[-10%] left-1/2 -translate-x-1/2 h-72 w-[90%] max-w-4xl rounded-[48px] bg-gradient-to-r from-blue-100/60 to-sky-100/60 blur-3xl" />
    </div>

    <div className="max-w-3xl mx-auto">
      <Badge />
      <Headline />
      <Subcopy />
      <CTAs />

      <div className="mt-12 grid grid-cols-3 gap-6 sm:gap-8">
        {[
          { n: '12k+', l: 'Trained' },
          { n: '20+', l: 'Years' },
          { n: '1000+', l: 'Sessions' },
        ].map((s) => (
          <div key={s.l} className="rounded-2xl bg-white/70 dark:bg-white/5 backdrop-blur border border-blue-100/70 dark:border-white/10 shadow-sm p-4">
            <div className="text-2xl font-semibold text-blue-700">{s.n}</div>
            <div className="text-sm text-gray-500">{s.l}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Hero;
